/**
 * The airship's procedural sky and sea of clouds (ADR 0033, lifted from
 * brad-airship `airship-sky.js`): one full-screen triangle at the far plane,
 * computed from a "matte camera" ray. The matte camera shares the real
 * camera's yaw but a wider, lens-shifted frustum, so the horizon always sits
 * high in frame (an Octopath-style painted backdrop) though the camera looks
 * down at about 24°. Only uniforms change per frame; the noise is the weather
 * field's (`weather.ts`).
 *
 * Layers, far to near: the gradient sky with the sun, moon, stars and high
 * cirrus; towering cumulus on the horizon; a hazy mountain range; the cloud
 * sea; three floating islands; horizon mist bands. It is drawn after the
 * opaque ship at the far plane with a depth test, so early-Z skips every
 * pixel the hull, rig and jetty cover (the GPU pass's 1.6 ms cut).
 *
 * The time of day's sky (`SkyLook`) sets the colours and amounts (`apply`);
 * the preset's sun and moon azimuths are relative to the camera's heading
 * (the painted composition holds in every framing). Medium only: at Off the
 * scene's background is the time of day's flat colour.
 */
import * as THREE from 'three';

const vertexShader = 'varying vec2 vNdc;void main(){vNdc=position.xy;gl_Position=vec4(position.xy,1.,1.);}';

const fragmentShader = `
uniform sampler2D uNoise;
uniform vec3 uRight,uFwd;uniform vec2 uTan,uCloudOff,uRoll;uniform float uH,uPan,uTime,uIsleShift,uCamAz;
uniform vec3 sunDir,moonDir;uniform float sunSize,sunVeil,sunPower,moon,stars;
uniform vec3 cZenith,cHorizon,cSunGlow,cSun,cMoon;
uniform vec3 cSeaLit,cSeaShade,cSeaDeep,cSeaFar;uniform float seaCover;
uniform vec3 cTowerLit,cTowerShade,cRim;uniform float towerHeight,towerCover;
uniform vec3 cRange,cIsle,cIsleLit,cMist,cCirrus;uniform float mist,cirrus,rangeHaze,isleHaze,rays;
varying vec2 vNdc;
float hash(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}
vec4 nz(vec2 uv){return texture2D(uNoise,uv);}
float seaH(vec2 uv){return nz(uv).r*.62+nz(uv*2.7+vec2(.3,.7)).a*.38;}
void main(){
  vec2 sv=vec2((vNdc.x+uPan)*uTan.x,vNdc.y*uTan.y);
  sv=vec2(uRoll.x*sv.x-uRoll.y*sv.y,uRoll.y*sv.x+uRoll.x*sv.y);
  vec3 v=vec3(sv.x,sv.y-uH,-1.);
  vec3 dir=normalize(uRight*v.x+vec3(0.,1.,0.)*v.y+uFwd*-v.z);
  float e=dir.y;
  vec2 flat2=normalize(dir.xz+1e-5);
  float az=atan(dir.x,-dir.z)-uCamAz;
  az=mod(az+3.14159,6.28318)-3.14159;
  vec2 sunFlat=normalize(sunDir.xz+1e-5);
  float sd=max(dot(dir,sunDir),0.);
  float sunAz=max(dot(flat2,sunFlat),0.);
  vec3 rightW=vec3(-flat2.y,0.,flat2.x);
  float s=clamp(e/.3,0.,1.);
  vec3 col=mix(cHorizon,cZenith,smoothstep(0.,1.,pow(s,.6)));
  col+=cSunGlow*(pow(sd,5.)*.45+pow(sd,48.)*1.1)*sunPower;
  col+=cSunGlow*exp(-abs(e)*14.)*pow(sunAz,4.)*.6*sunPower;
  if(stars>0.&&e>0.){
    vec2 g=vec2(az,e)*90.;vec2 c=floor(g);float h=hash(c);
    vec2 o=vec2(hash(c+7.1),hash(c+3.7))*.5+.25;
    float st=(1.-smoothstep(.0,.16,length(fract(g)-o)))*step(.988,h)*(.65+.35*sin(uTime*(1.+h*3.)+h*40.));
    col+=vec3(.8,.88,1.)*st*stars*(1.+(h-.988)*260.)*smoothstep(.0,.08,e);
  }
  if(moon>0.){
    float md=dot(dir,moonDir);
    float disc=smoothstep(cos(.03),cos(.027),md);
    float mare=.8+.2*nz((dir.xz-moonDir.xz)*7.).r;
    col+=cMoon*(disc*2.6*mare+pow(max(md,0.),1500.)*.5+pow(max(md,0.),90.)*.16+pow(max(md,0.),10.)*.07)*moon;
  }
  if(e>0.&&cirrus>0.){
    vec2 cp=vec2(az*.9,.05/(e+.03))+uCloudOff*vec2(.2,0.);
    float n=nz(vec2(cp.x*.5,cp.y*.9)).g*.6+nz(vec2(cp.x*1.3,cp.y*2.2)+.4).r*.4;
    float c=smoothstep(.48,.74,n)*cirrus*smoothstep(.0,.06,e);
    vec3 cc=mix(cCirrus,cSunGlow*1.3,pow(sd,4.)*.9+.3*(1.-smoothstep(.48,.66,n))*sunPower);
    col=mix(col,cc,c);
  }
  float disc=smoothstep(cos(sunSize*1.25),cos(sunSize),sd);
  col+=cSun*disc*(1.-sunVeil*.85)*sunPower;
  if(towerHeight>0.&&e<towerHeight*1.05&&e>-.02){
    float cw=.26,cx=az/cw+uIsleShift*.6,ci=floor(cx);
    float best=0.;vec2 bd=vec2(0.);float field=0.;
    float edge=(nz(vec2(az,e)*vec2(7.,11.)).a-.5)*.5;
    for(int k=-1;k<=1;k++){
      float id=ci+float(k);
      if(hash(vec2(id,1.3))<towerCover)continue;
      float H=towerHeight*(.25+.75*pow(hash(vec2(id,2.7)),1.6));
      float W=H*(1.3+.9*hash(vec2(id,4.9)));
      float xc=(id+.5+(hash(vec2(id,3.1))-.5)*.6)*cw-uIsleShift*.6*cw;
      if(abs(az-xc)>W*.48+H*.41||e>H*1.19)continue;
      for(int b=0;b<8;b++){
        float fb=float(b),j=hash(vec2(id,20.+fb));
        vec2 c;float r;
        if(b<5){float th=3.14159*(.12+.76*fb/4.)+(j-.5)*.3;c=vec2(cos(th)*W*.42,H*.42+sin(th)*H*.36);r=H*(.26+.14*hash(vec2(id,10.+fb)));}
        else{c=vec2((fb-6.)*W*.4+(j-.5)*W*.15,H*.2);r=H*(.24+.08*j);}
        vec2 d=vec2(az-xc-c.x,e-c.y);d.y=min(d.y,d.y*2.4);d/=r;
        float f=1.-dot(d,d);
        if(f>best){best=f;bd=d;}
      }
    }
    field=best+edge*.2;
    if(field>0.){
      vec3 n=vec3(bd,sqrt(max(1.-dot(bd,bd),0.)));
      vec3 nW=normalize(rightW*n.x+vec3(0.,n.y,0.)-vec3(flat2.x,0.,flat2.y)*n.z);
      float puff=nz(vec2(az,e)*vec2(16.,24.)).a;
      float dif=clamp(dot(nW,sunDir)*.55+.42+puff*.22-.1,0.,1.);
      float back=pow(sunAz,3.)*smoothstep(.0,.15,sunDir.y+.1);
      vec3 c=mix(cTowerShade,cTowerLit,dif*(1.-back*.75));
      vec2 s2=normalize(vec2(atan(sunDir.x,-sunDir.z)-uCamAz-az,sunDir.y-e));
      float facing=smoothstep(-.3,.9,dot(normalize(bd+1e-4),s2));
      c+=cRim*(1.-smoothstep(.0,.2,field))*(.15+back*1.8)*facing*sunPower;
      c=mix(c,cHorizon,.16+.55*exp(-max(e,0.)*38.));
      col=mix(col,c,smoothstep(0.,.14,field));
    }
  }
  {
    float u=az*.42+uPan*.08+.3;
    float r=nz(vec2(u,.83)).b,r2=nz(vec2(u*3.1,.47)).b;
    float rh=.004+.055*pow(r,2.6)+.012*r2*r;
    if(e<rh){
      float face=smoothstep(-.03,.03,nz(vec2(u*3.1+.004,.47)).b-r2);
      vec3 rc=mix(cRange,cRange*1.3+cSunGlow*.1*sunPower,face);
      rc=mix(rc,cHorizon,rangeHaze*(1.-smoothstep(0.,.05,rh-e)*.35));
      col=mix(col,rc,smoothstep(0.,.0025,rh-e));
    }
  }
  if(e<.002){
    float t=1./max(-e,.004);
    vec2 p=dir.xz*t;
    vec2 uv=p*.045+uCloudOff;
    float h=seaH(uv);
    float hx=seaH(uv+vec2(.006,0.)),hz=seaH(uv+vec2(0.,.006));
    vec3 N=normalize(vec3((h-hx)*22.,1.,(h-hz)*22.));
    float body=smoothstep(seaCover-.08,seaCover+.12,h);
    float dif=clamp(dot(N,normalize(sunDir+vec3(0.,.12,0.)))*.7+.35,0.,1.);
    float top=smoothstep(seaCover,seaCover+.35,h);
    vec3 sc=mix(cSeaShade,cSeaLit,dif*(.55+.45*top));
    sc=mix(cSeaDeep,sc,body);
    sc+=cRim*pow(sd,5.)*(1.-abs(body*2.-1.))*.6*sunPower;
    sc+=cSunGlow*pow(sd,6.)*.45*(.35+.65*body)*sunPower;
    if(moon>0.){float mg=pow(max(dot(flat2,normalize(moonDir.xz+1e-5)),0.),40.);sc+=cMoon*mg*exp(e*6.)*.3*moon*body;}
    float far=1.-exp(-t*.08);
    sc=mix(sc,cSeaDeep,(1.-smoothstep(-.6,-.12,e))*.55);
    col=mix(sc,cSeaFar,smoothstep(.1,1.,far));
    col=mix(col,cHorizon,smoothstep(.94,1.,far));
  }
  for(int i=0;i<3;i++){
    float fi=float(i);
    float a0=i==0?-.36:i==1?.3:-.12; float w=i==0?.075:i==1?.05:.032;
    float top=i==0?.05:i==1?.075:.026; float thick=i==0?.08:i==1?.05:.032;
    float haze=isleHaze*(i==0?.5:i==1?.7:.9);
    float ac=mod(a0-uIsleShift*(1.3-fi*.35)+uPan*(.10-fi*.03)+3.14159,6.28318)-3.14159;
    float x=(az-ac)/w;
    if(abs(x)<1.2){
      float ax=abs(x);
      float trees=pow(nz(vec2(x*1.4+fi*.37,.21)).a,1.5)*.32*(1.-smoothstep(.55,1.,ax));
      float tp=top+w*(.1*sqrt(max(1.-x*x,0.))+trees);
      float spur=nz(vec2(x*.55+fi*.2,.71)).b;
      float bowl=sqrt(max(1.-x*x,0.));
      float bt=top-thick*(bowl*.42+pow(max(1.-ax*1.15,0.),2.2)*(.55+.5*spur)+.12*spur*bowl);
      if(e<tp&&e>bt&&ax<1.){
        float y=(e-bt)/max(top-bt,1e-3);
        float side=x*sign(dot(sunFlat,rightW.xz));
        float strata=.75+.25*nz(vec2(x*1.5,e*60.)).g;
        vec3 ic=cIsle*strata*(.45+.55*clamp(y,0.,1.))*(1.+.5*side*sunPower);
        float crown=smoothstep(top-.004,top+.002,e);
        ic=mix(ic,cIsleLit*(.8+.4*nz(vec2(x*3.,e*50.)).a)*(1.+.3*side*sunPower),crown);
        ic=mix(ic,cHorizon*.9+cSunGlow*.06,haze);
        col=mix(col,ic,smoothstep(0.,.002,min(tp-e,e-bt)));
      }
      if(i==0){float wf=(1.-smoothstep(.0,.03,abs(x-.32)))*step(e,bt+.01)*smoothstep(bt-.1,bt,e)*(.6+.4*nz(vec2(x*4.,e*30.-uTime*.3)).g);
        col=mix(col,mix(cIsleLit,cHorizon,.4)*1.15,wf*.6*(1.-haze));}
    }
  }
  if(rays>0.){
    vec2 rel=vec2(az-(atan(sunDir.x,-sunDir.z)-uCamAz),e-sunDir.y);
    float r=length(rel),ang=atan(rel.y,rel.x);
    float ray=smoothstep(.42,.85,nz(vec2(ang*1.3+uTime*.002,.55)).g)*(.6+.4*nz(vec2(ang*4.1,.35)).r);
    col+=cSunGlow*ray*rays*exp(-r*2.6)*(1.-exp(-r*25.))*sunPower*(1.-sunVeil);
  }
  if(mist>0.){
    float band=exp(-abs(e+.012)*34.)*.8+exp(-abs(e+.05)*20.)*.5*nz(vec2(az*.3+uTime*.002,.9)).r;
    col=mix(col,cMist,clamp(band*mist,0.,.9));
  }
  gl_FragColor=vec4(min(col,vec3(3.)),1.);
}`;

/** The sky's colours (`airship-sky.js` `COLOR_KEYS`, named as the content's `sky.colors`). */
export const skyColorKeys = ['zenith', 'horizon', 'sunGlow', 'sun', 'moon', 'seaLit', 'seaShade', 'seaDeep', 'seaFar', 'towerLit', 'towerShade', 'rim', 'range', 'isle', 'isleLit', 'mist', 'cirrus'] as const;
export type SkyColorKey = (typeof skyColorKeys)[number];
/** The sky's amounts (`NUMBER_KEYS`). */
export const skyNumberKeys = ['sunSize', 'sunPower', 'moon', 'stars', 'seaCover', 'towerHeight', 'towerCover', 'mist', 'cirrus', 'rangeHaze', 'isleHaze', 'rays'] as const;
export type SkyNumberKey = (typeof skyNumberKeys)[number];

/** One time of day's sky, in three.js values (blendable). */
export type SkyLook = {
  readonly sunDir: THREE.Vector3;
  readonly moonDir: THREE.Vector3;
  readonly colors: Readonly<Record<SkyColorKey, THREE.Color>>;
  readonly numbers: Record<SkyNumberKey, number>;
};

/** The shader's uniform for a colour key (`c` + its capitalised name: `cZenith`). */
const colorUniform = (k: SkyColorKey): string => `c${k.charAt(0).toUpperCase()}${k.slice(1)}`;

export type ShipSky = {
  readonly mesh: THREE.Mesh;
  apply(look: SkyLook): void;
  update(dt: number, camera: THREE.PerspectiveCamera, options: { readonly time: number; readonly driftX: number; readonly driftZ: number; readonly flight: number; readonly veil: number; readonly attitude: { readonly roll: number; readonly pitch: number; readonly heave: number } }): void;
  /** Screen uv of the sky's sun (for the lens glare), or null when off frame. */
  sunScreen(camera: THREE.PerspectiveCamera): THREE.Vector2 | null;
  dispose(): void;
};

export const createShipSky = (noise: THREE.Texture): ShipSky => {
  const uniforms: Record<string, THREE.IUniform> = {
    uNoise: { value: noise },
    uRight: { value: new THREE.Vector3(1, 0, 0) },
    uFwd: { value: new THREE.Vector3(0, 0, -1) },
    uTan: { value: new THREE.Vector2(0.8, 0.5) },
    uCloudOff: { value: new THREE.Vector2() },
    uH: { value: 0.22 },
    uPan: { value: 0 },
    uTime: { value: 0 },
    uIsleShift: { value: 0 },
    uCamAz: { value: 0 },
    uRoll: { value: new THREE.Vector2(1, 0) },
    sunDir: { value: new THREE.Vector3(0, 0.1, -1).normalize() },
    moonDir: { value: new THREE.Vector3(0.3, 0.2, -1).normalize() },
    sunVeil: { value: 0 },
  };
  for (const k of skyColorKeys) uniforms[colorUniform(k)] = { value: new THREE.Color() };
  for (const k of skyNumberKeys) uniforms[k] = { value: 0 };
  const material = new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader, depthTest: true, depthWrite: false });
  material.name = 'airship-sky';
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'Procedural sky and cloud sea';
  mesh.frustumCulled = false;
  mesh.renderOrder = 1000;
  const uniform = (name: string): THREE.IUniform => {
    const slot = uniforms[name];
    if (slot === undefined) throw new Error(`sky: no uniform ${name}`);
    return slot;
  };
  const vec3 = (name: string): THREE.Vector3 => uniform(name).value as THREE.Vector3;
  const vec2 = (name: string): THREE.Vector2 => uniform(name).value as THREE.Vector2;
  const color = (name: string): THREE.Color => uniform(name).value as THREE.Color;
  const scalar = (name: string): number => uniform(name).value as number;
  const fwd = new THREE.Vector3();
  const sunUv = new THREE.Vector2();
  const baseSun = new THREE.Vector3();
  const baseMoon = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const orient = (): void => {
    const a = -scalar('uCamAz');
    vec3('sunDir').copy(baseSun).applyAxisAngle(up, a);
    vec3('moonDir').copy(baseMoon).applyAxisAngle(up, a);
  };
  let cloudX = 0;
  let cloudZ = 0;
  let isle = 0;
  const set = (name: string, value: number): void => {
    (uniforms[name] as THREE.IUniform<number>).value = value;
  };
  return {
    mesh,
    apply(look) {
      for (const k of skyColorKeys) color(colorUniform(k)).copy(look.colors[k]);
      for (const k of skyNumberKeys) set(k, look.numbers[k]);
      baseSun.copy(look.sunDir).normalize();
      baseMoon.copy(look.moonDir).normalize();
      orient();
    },
    update(dt, camera, { time, driftX, driftZ, flight, veil, attitude }) {
      camera.getWorldDirection(fwd);
      fwd.y = 0;
      if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
      fwd.normalize();
      vec3('uFwd').copy(fwd);
      vec3('uRight').set(-fwd.z, 0, fwd.x);
      const camAz = Math.atan2(fwd.x, -fwd.z);
      if (Math.abs(camAz - scalar('uCamAz')) > 1e-4) {
        set('uCamAz', camAz);
        orient();
      }
      const tanY = 0.5;
      const aspect = camera.aspect;
      vec2('uTan').set(tanY * aspect, tanY);
      // Horizon at about 70% of the frame on landscape, a little higher on portrait; the camera rides the ship's attitude.
      set('uH', tanY * (aspect < 0.8 ? 0.5 : 0.4) - Math.tan(attitude.pitch) + attitude.heave * 0.01);
      vec2('uRoll').set(Math.cos(attitude.roll), Math.sin(attitude.roll));
      set('uPan', THREE.MathUtils.clamp((camera.position.x - 40) * 0.004, -0.25, 0.25));
      set('uTime', time);
      cloudX += driftX * dt * 0.0035;
      cloudZ += driftZ * dt * 0.0035;
      cloudX %= 40;
      cloudZ %= 40;
      vec2('uCloudOff').set(-cloudX, -cloudZ);
      isle += flight * dt * 0.012;
      set('uIsleShift', isle % (Math.PI * 2));
      set('sunVeil', veil);
    },
    sunScreen() {
      const d = vec3('sunDir');
      const vx = d.dot(vec3('uRight'));
      const vz = d.dot(vec3('uFwd'));
      if (vz <= 0.05) return null;
      const r = vec2('uRoll');
      const px = vx / vz;
      const py = d.y / vz + scalar('uH');
      // Undo the shader's roll (view = R(roll) × screen).
      const sx = r.x * px + r.y * py;
      const sy = -r.y * px + r.x * py;
      const tan = vec2('uTan');
      return sunUv.set((sx / tan.x - scalar('uPan')) * 0.5 + 0.5, (sy / tan.y) * 0.5 + 0.5);
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
};
